import {useEffect} from 'react'
import {useLocation, Outlet, useNavigate} from 'react-router-dom'
import {jwtDecode} from 'jwt-decode'

import SessionController from './SessionController'

import {buildRoute, isEmpty} from '../../lib/tools'
import {getToken, setSessionTokens, removeToken} from '../../lib/auth'
import {readEndpoint, createResource} from '../../lib/asyncMethods'
import {connect} from '../../state/hooks/tools'
import {USERS, SESSION} from '../../state/actionTypes'

const AuthGateway = props => {
  const location = useLocation()
  const navigate = useNavigate()

  const redirectToSignIn = ({clearAuthToken = false, clearRecoveryTokens = false} = {}) => {
    if (clearAuthToken)
      removeToken('authToken')

    if (clearRecoveryTokens) {
      removeToken('refreshToken')
      removeToken('trustToken')
    }

    navigate(buildRoute({route: '/auth/sign-in', location}))
  }

  const refreshSession = () => {
    const refreshToken = getToken('refreshToken')
    const authToken = getToken('authToken')

    if (!refreshToken) {
      redirectToSignIn({clearAuthToken: true})

      return Promise.resolve(false)
    }

    return props.createResource({endpoint: 'auth/refresh', attributes: {refreshToken, authToken}})
      .then(response => {
        if (response.success) {
          setSessionTokens(response.data)

          return true
        }

        redirectToSignIn({clearAuthToken: true, clearRecoveryTokens: true})

        return false
      })
      .catch(() => {
        redirectToSignIn({clearAuthToken: true, clearRecoveryTokens: true})

        return false
      })
  }

  const fetchCurrentUser = ({allowRefresh = true} = {}) => {
    const currentAuthToken = getToken('authToken')

    if (!currentAuthToken) {
      if (allowRefresh) {
        return refreshSession().then(refreshed => {
          if (refreshed)
            return fetchCurrentUser({allowRefresh: false})

          return null
        })
      }

      redirectToSignIn({clearAuthToken: true})

      return Promise.resolve(null)
    }

    try {
      const {sub} = jwtDecode(currentAuthToken)

      return props.readEndpoint(`users/${sub}`)
        .then(response => {
          if (!response.success) {
            if (allowRefresh) {
              return refreshSession().then(refreshed => {
                if (refreshed)
                  return fetchCurrentUser({allowRefresh: false})

                return response
              })
            }

            redirectToSignIn({clearAuthToken: true})
          }

          return response
        })
        .catch(() => {
          redirectToSignIn({clearAuthToken: true})

          return null
        })
    } catch (error) {
      if (__DEV__)
        console.error(error)

      if (allowRefresh) {
        return refreshSession().then(refreshed => {
          if (refreshed)
            return fetchCurrentUser({allowRefresh: false})

          return null
        })
      }

      redirectToSignIn({clearAuthToken: true})

      return Promise.resolve(null)
    }
  }

  useEffect(() => {
    if (isEmpty(props.currentUser)) {
      fetchCurrentUser()
    }
  }, [])

  return (
    <SessionController currentUser={props.currentUser} location={location} navigate={navigate}>
      <Outlet />
    </SessionController>
  )
}

const mapStateToProps = state => ({...state.users})

const actionCreators = ({auth, users}) => ({
  readEndpoint: readEndpoint(users, USERS),
  createResource: createResource(auth, SESSION)
})

export default connect(mapStateToProps, actionCreators)(AuthGateway)
